import React, { useCallback, useEffect, useState } from 'react';
import { blockedForTrial } from './TrialUpgrade';
import jsPDF from 'jspdf';

// Phase 1 monitoring "statement": a historical, downloadable report of every
// signal for a project over a date range — including previously-cleared alerts
// (they're soft-deleted, so the history is preserved server-side). Backed by
// GET /api/monitoring/projects/:id/report (json for preview, csv for download).

interface MonitoringReportProps {
  projectId: number;
  projectName?: string;
  onClose: () => void;
}

interface ReportSignal {
  id: number;
  date: string;
  type: string;
  type_label: string;
  priority: string;
  status: string;
  candidate_name: string | null;
  candidate_job_title: string | null;
  candidate_company: string | null;
  candidate_linkedin_url: string | null;
  title: string | null;
  company: string | null;
  from_company: string | null;
  to_company: string | null;
  move_date: string | null;
  position: string | null;
  risk_score: number | null;
  detail: string | null;
}

interface MovementPerson {
  name: string;
  title: string | null;
  from?: string | null;
  to?: string | null;
  date: string;
  linkedin: string | null;
}
interface CompanyMovementGroup {
  company: string;
  count: number;
  industry?: string | null;
  employee_count?: number | null;
  people: MovementPerson[];
}

interface ReportData {
  project: { id: number; name: string };
  period: { from: string; to: string };
  summary: {
    total: number;
    byType: Record<string, number>;
    byPriority: Record<string, number>;
    totalMovements?: number;
    companiesGaining?: number;
    companiesLosing?: number;
  };
  signals: ReportSignal[];
  movements?: any[];
  arrivals?: CompanyMovementGroup[];
  departures?: CompanyMovementGroup[];
}

// Movement signal types are now shown richly in the Arrivals / Backfills
// sections, so we hide them from the generic per-type signal list to avoid
// one long undifferentiated blur.
const MOVEMENT_TYPES = ['job_change', 'promotion', 'company_movement'];

type RGB = [number, number, number];
// Single source of truth for a signal type's label + brand colour, shared by
// the on-screen preview and the PDF so both read as one designed document.
const TYPE_META: Record<string, { label: string; color: RGB }> = {
  job_change: { label: 'Job Changes', color: [37, 99, 235] },
  promotion: { label: 'Promotions', color: [139, 92, 246] },
  company_movement: { label: 'Company Movements', color: [13, 148, 136] },
  open_to_work: { label: 'Open to Work', color: [22, 163, 74] },
  critical_risk_candidate: { label: 'Critical Flight Risk', color: [220, 38, 38] },
  high_risk_candidate: { label: 'High Flight Risk', color: [234, 88, 12] },
  profile_change: { label: 'Profile Updates', color: [100, 116, 139] },
  new_hire_detected: { label: 'New Hires', color: [8, 145, 178] },
  company_hiring_trend: { label: 'Company Trends', color: [79, 70, 229] },
};
const TYPE_ORDER = [
  'job_change', 'promotion', 'company_movement', 'open_to_work',
  'critical_risk_candidate', 'high_risk_candidate', 'profile_change',
  'new_hire_detected', 'company_hiring_trend',
];
const TYPE_OPTIONS = TYPE_ORDER.map(v => ({ value: v, label: TYPE_META[v].label }));
const metaFor = (t: string): { label: string; color: RGB } => TYPE_META[t] || { label: t, color: [100, 116, 139] };
const cssRgb = (c: RGB) => `rgb(${c[0]}, ${c[1]}, ${c[2]})`;

function isoDay(d: Date): string {
  return d.toISOString().slice(0, 10);
}

// A one-line human summary of a signal for the preview / PDF.
function signalDetail(s: ReportSignal): string {
  if (s.from_company || s.to_company) {
    const from = s.from_company || '?';
    const to = s.to_company || '?';
    return `${from} → ${to}${s.move_date ? ` (${s.move_date})` : ''}`;
  }
  if (s.risk_score != null) return `Risk score ${s.risk_score}${s.company ? ` · ${s.company}` : ''}`;
  if (s.detail) return s.detail;
  if (s.company) return s.company;
  return s.title || '';
}

const MonitoringReport: React.FC<MonitoringReportProps> = ({ projectId, projectName, onClose }) => {
  const today = new Date();
  const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

  const [from, setFrom] = useState<string>(isoDay(monthAgo));
  const [to, setTo] = useState<string>(isoDay(today));
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]); // empty = all
  const [data, setData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState<'' | 'csv' | 'pdf'>('');
  const [error, setError] = useState<string | null>(null);

  const buildParams = useCallback(() => {
    const p = new URLSearchParams();
    p.append('from', from);
    p.append('to', to);
    if (selectedTypes.length > 0) p.append('types', selectedTypes.join(','));
    return p;
  }, [from, to, selectedTypes]);

  const generate = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/monitoring/projects/${projectId}/report?${buildParams().toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      const json = await res.json();
      setData(json);
    } catch (e: any) {
      setError(e?.message || 'Failed to generate report');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [projectId, buildParams]);

  // Auto-generate on open for the default (last 30 days) window.
  useEffect(() => { generate(); /* eslint-disable-next-line */ }, []);

  const toggleType = (value: string) => {
    setSelectedTypes(prev => (prev.indexOf(value) >= 0 ? prev.filter(t => t !== value) : prev.concat(value)));
  };

  const downloadCSV = async () => {
    if (blockedForTrial()) return;
    setDownloading('csv');
    try {
      const token = localStorage.getItem('token');
      const params = buildParams();
      params.append('format', 'csv');
      const res = await fetch(`/api/monitoring/projects/${projectId}/report?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `monitoring-report-${from}_to_${to}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(e?.message || 'CSV download failed');
    } finally {
      setDownloading('');
    }
  };

  const downloadPDF = () => {
    if (blockedForTrial()) return;
    if (!data) return;
    setDownloading('pdf');
    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const margin = 14;
      const contentW = pageW - margin * 2;
      const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
      const setFill = (c: RGB) => doc.setFillColor(c[0], c[1], c[2]);
      const setText = (c: RGB) => doc.setTextColor(c[0], c[1], c[2]);

      // ---- Header band ----
      setFill([37, 99, 235]);
      doc.rect(0, 0, pageW, 34, 'F');
      setText([255, 255, 255]);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(19);
      doc.text('Monitoring Intelligence Report', margin, 15);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.text(data.project.name, margin, 23);
      doc.setFontSize(9);
      doc.text(`${fmtDate(data.period.from)} – ${fmtDate(data.period.to)}`, margin, 29);
      doc.text(`Generated ${new Date().toLocaleDateString('en-GB')}`, pageW - margin, 29, { align: 'right' });

      let y = 44;

      // ---- Executive summary cards ----
      const critical = data.summary.byType['critical_risk_candidate'] || 0;
      const high = data.summary.byType['high_risk_candidate'] || 0;
      const cards: { label: string; value: number; color: RGB }[] = [
        { label: 'Total Signals', value: data.summary.total, color: [37, 99, 235] },
        { label: 'Job Changes', value: data.summary.byType['job_change'] || 0, color: metaFor('job_change').color },
        { label: 'Open to Work', value: data.summary.byType['open_to_work'] || 0, color: metaFor('open_to_work').color },
        { label: 'Flight Risk', value: critical + high, color: [220, 38, 38] },
        { label: 'Promotions', value: data.summary.byType['promotion'] || 0, color: metaFor('promotion').color },
        { label: 'Company Trends', value: data.summary.byType['company_hiring_trend'] || 0, color: metaFor('company_hiring_trend').color },
      ];
      const cols = 3;
      const gap = 4;
      const cardW = (contentW - gap * (cols - 1)) / cols;
      const cardH = 20;
      cards.forEach((c, i) => {
        const x = margin + (i % cols) * (cardW + gap);
        const cy = y + Math.floor(i / cols) * (cardH + gap);
        setFill([255, 255, 255]);
        doc.setDrawColor(226, 232, 240);
        doc.roundedRect(x, cy, cardW, cardH, 2, 2, 'FD');
        setFill(c.color);
        doc.circle(x + 5, cy + 6.6, 1.3, 'F');
        setText([100, 116, 139]);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.5);
        doc.text(c.label.toUpperCase(), x + 8.5, cy + 7.4);
        setText(c.color);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(17);
        doc.text(String(c.value), x + 5, cy + 16);
      });
      y += Math.ceil(cards.length / cols) * (cardH + gap) + 6;

      // ---- Signals by type (horizontal bars) ----
      const typeCounts = Object.keys(data.summary.byType)
        .map(t => ({ t, n: data.summary.byType[t] }))
        .sort((a, b) => b.n - a.n);
      if (typeCounts.length > 0) {
        setText([17, 24, 39]);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.text('Signals by Type', margin, y);
        y += 6;
        const maxN = Math.max.apply(null, typeCounts.map(x => x.n));
        const labelW = 44;
        const barMaxW = contentW - labelW - 12;
        doc.setFont('helvetica', 'normal');
        typeCounts.forEach(({ t, n }) => {
          const m = metaFor(t);
          if (y > pageH - margin - 8) { doc.addPage(); y = margin; }
          doc.setFontSize(8);
          setText([55, 65, 81]);
          doc.text(m.label, margin, y + 3.3);
          const w = maxN > 0 ? (n / maxN) * barMaxW : 0;
          setFill(m.color);
          doc.roundedRect(margin + labelW, y, Math.max(w, 1.5), 4.6, 1, 1, 'F');
          setText([71, 85, 105]);
          doc.text(String(n), margin + labelW + Math.max(w, 1.5) + 2, y + 3.5);
          y += 7;
        });
        y += 5;
      }

      // ---- Talent Arrivals & Backfill Detections (rich per-person detail) ----
      const renderMovementSection = (title: string, groups: CompanyMovementGroup[] | undefined, accent: RGB, dir: 'in' | 'out') => {
        if (!groups || groups.length === 0) return;
        if (y > pageH - margin - 18) { doc.addPage(); y = margin; }
        y += 2;
        setFill(accent);
        doc.roundedRect(margin, y, contentW, 8, 1.5, 1.5, 'F');
        setText([255, 255, 255]);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.text(title, margin + 3, y + 5.4);
        doc.text(`${groups.length} companies`, pageW - margin - 3, y + 5.4, { align: 'right' });
        y += 11;
        groups.slice(0, 20).forEach(g => {
          if (y > pageH - margin - 12) { doc.addPage(); y = margin; }
          setText([17, 24, 39]);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(9.5);
          const sign = dir === 'in' ? '+' : '-';
          doc.text(`${g.company}  ${sign}${g.count}${g.industry ? `  ·  ${g.industry}` : ''}`, margin + 1, y + 3);
          y += 5;
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8);
          setText([71, 85, 105]);
          g.people.slice(0, 8).forEach(p => {
            const other = dir === 'in' ? (p.from ? `from ${p.from}` : '') : (p.to ? `joined ${p.to}` : '');
            const when = p.date ? ` (${new Date(p.date).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })})` : '';
            const line = `•  ${p.name}${p.title ? ` — ${p.title}` : ''}${other ? ` · ${other}` : ''}${when}`;
            const wrapped = doc.splitTextToSize(line, contentW - 8);
            if (y + wrapped.length * 3.6 > pageH - margin - 8) { doc.addPage(); y = margin; }
            doc.text(wrapped, margin + 4, y + 2.6);
            y += wrapped.length * 3.6 + 1;
          });
          if (g.people.length > 8) {
            setText([148, 163, 184]);
            doc.setFontSize(7.5);
            doc.text(`   …and ${g.people.length - 8} more`, margin + 4, y + 2.6);
            y += 4;
          }
          y += 2;
        });
        y += 2;
      };
      renderMovementSection('Talent Arrivals — where talent is going', data.arrivals, [13, 148, 136], 'in');
      renderMovementSection('Backfill Detections — companies losing talent', data.departures, [234, 88, 12], 'out');

      // ---- Per-type detail sections for the remaining (non-movement) signals ----
      const grouped: Record<string, ReportSignal[]> = {};
      data.signals.forEach(s => { (grouped[s.type] = grouped[s.type] || []).push(s); });
      const orderedTypes = Object.keys(grouped)
        .filter(t => MOVEMENT_TYPES.indexOf(t) < 0)
        .sort((a, b) => grouped[b].length - grouped[a].length);

      orderedTypes.forEach(type => {
        const list = grouped[type];
        const m = metaFor(type);
        if (y > pageH - margin - 18) { doc.addPage(); y = margin; }
        y += 2;
        setFill(m.color);
        doc.roundedRect(margin, y, contentW, 8, 1.5, 1.5, 'F');
        setText([255, 255, 255]);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.text(m.label, margin + 3, y + 5.4);
        doc.text(String(list.length), pageW - margin - 3, y + 5.4, { align: 'right' });
        y += 11;

        doc.setFont('helvetica', 'normal');
        list.forEach((s, idx) => {
          const name = s.candidate_name || s.company || '—';
          const detail = signalDetail(s);
          const line = `${name}${detail ? ` — ${detail}` : ''}`;
          const wrapped = doc.splitTextToSize(line, contentW - 26);
          const rowH = Math.max(6, wrapped.length * 4 + 1.5);
          if (y + rowH > pageH - margin - 8) { doc.addPage(); y = margin; }
          if (idx % 2 === 0) {
            setFill([248, 250, 252]);
            doc.rect(margin, y - 1, contentW, rowH, 'F');
          }
          setText([148, 163, 184]);
          doc.setFontSize(7.5);
          doc.text(new Date(s.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }), margin + 2, y + 3.2);
          const pri = s.priority;
          const dot: RGB = pri === 'critical' ? [220, 38, 38] : pri === 'high' ? [234, 88, 12] : pri === 'medium' ? [37, 99, 235] : [148, 163, 184];
          setFill(dot);
          doc.circle(margin + 20, y + 2.2, 0.9, 'F');
          setText([31, 41, 55]);
          doc.setFontSize(8.5);
          doc.text(wrapped, margin + 24, y + 3.2);
          y += rowH;
        });
        y += 3;
      });

      // ---- Footer on every page ----
      const totalPages = doc.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(7.5);
        setText([156, 163, 175]);
        doc.text(data.project.name, margin, pageH - 8);
        doc.text(`Page ${i} of ${totalPages}`, pageW / 2, pageH - 8, { align: 'center' });
        doc.text('Powered by MarketMapr', pageW - margin, pageH - 8, { align: 'right' });
      }

      doc.save(`monitoring-report-${from}_to_${to}.pdf`);
    } catch (e: any) {
      setError(e?.message || 'PDF generation failed');
    } finally {
      setDownloading('');
    }
  };

  // Group preview signals by type for display.
  const grouped: Record<string, ReportSignal[]> = {};
  if (data) data.signals.forEach(s => { (grouped[s.type] = grouped[s.type] || []).push(s); });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Monitoring Report</h2>
            <p className="text-sm text-gray-500">{projectName || `Project ${projectId}`}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>

        {/* Controls */}
        <div className="px-6 py-4 border-b border-gray-100 space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
              <input type="date" value={from} max={to} onChange={e => setFrom(e.target.value)}
                className="border border-gray-300 rounded-md px-2 py-1.5 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
              <input type="date" value={to} min={from} onChange={e => setTo(e.target.value)}
                className="border border-gray-300 rounded-md px-2 py-1.5 text-sm" />
            </div>
            <button onClick={generate} disabled={loading}
              className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50">
              {loading ? 'Generating…' : 'Generate'}
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {TYPE_OPTIONS.map(opt => {
              const active = selectedTypes.indexOf(opt.value) >= 0;
              return (
                <button key={opt.value} onClick={() => toggleType(opt.value)}
                  className={`px-2.5 py-1 text-xs rounded-full border ${active ? 'bg-blue-50 border-blue-400 text-blue-700' : 'bg-white border-gray-300 text-gray-600'}`}>
                  {opt.label}
                </button>
              );
            })}
            {selectedTypes.length > 0 && (
              <button onClick={() => setSelectedTypes([])} className="px-2.5 py-1 text-xs text-gray-500 underline">
                Clear (all types)
              </button>
            )}
          </div>
        </div>

        {/* Preview */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {error && <div className="text-sm text-red-600 mb-3">{error}</div>}
          {loading && <div className="text-sm text-gray-500">Loading…</div>}
          {!loading && data && (
            <>
              <p className="text-sm text-gray-600 mb-4">
                <strong className="text-gray-900">{data.summary.total}</strong> signals detected
                between {new Date(data.period.from).toLocaleDateString('en-GB')} and {new Date(data.period.to).toLocaleDateString('en-GB')}.
              </p>

              {/* Executive summary cards — mirror the PDF */}
              {data.summary.total > 0 && (
                <div className="grid grid-cols-3 gap-2 mb-6">
                  {([
                    { label: 'Total Signals', value: data.summary.total, color: [37, 99, 235] as RGB },
                    { label: 'Job Changes', value: data.summary.byType['job_change'] || 0, color: metaFor('job_change').color },
                    { label: 'Open to Work', value: data.summary.byType['open_to_work'] || 0, color: metaFor('open_to_work').color },
                    { label: 'Flight Risk', value: (data.summary.byType['critical_risk_candidate'] || 0) + (data.summary.byType['high_risk_candidate'] || 0), color: [220, 38, 38] as RGB },
                    { label: 'Promotions', value: data.summary.byType['promotion'] || 0, color: metaFor('promotion').color },
                    { label: 'Company Trends', value: data.summary.byType['company_hiring_trend'] || 0, color: metaFor('company_hiring_trend').color },
                  ]).map(card => (
                    <div key={card.label} className="rounded-lg border border-gray-200 bg-white px-3 py-2">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: cssRgb(card.color) }} />
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">{card.label}</span>
                      </div>
                      <div className="text-xl font-bold" style={{ color: cssRgb(card.color) }}>{card.value}</div>
                    </div>
                  ))}
                </div>
              )}

              {data.summary.total === 0 && (
                <p className="text-sm text-gray-400">No signals in this period. Try widening the date range.</p>
              )}

              {/* Talent Arrivals — companies gaining talent, with per-person detail */}
              {data.arrivals && data.arrivals.length > 0 && (
                <div className="mb-6">
                  <div className="flex items-center justify-between rounded-md px-3 py-1.5 mb-2" style={{ backgroundColor: cssRgb([13, 148, 136]) }}>
                    <span className="text-xs font-bold uppercase tracking-wide text-white">Talent Arrivals · where talent is going</span>
                    <span className="text-xs font-bold text-white">{data.summary.companiesGaining} companies</span>
                  </div>
                  {data.arrivals.slice(0, 12).map(g => (
                    <div key={g.company} className="mb-2.5 pl-1">
                      <div className="text-sm font-semibold text-gray-900">
                        {g.company} <span className="text-teal-600 font-bold">+{g.count}</span>
                        {g.industry && <span className="text-xs text-gray-400 font-normal"> · {g.industry}</span>}
                      </div>
                      {g.people.slice(0, 6).map((p, i) => (
                        <div key={i} className="text-xs text-gray-600 pl-3">
                          <strong className="text-gray-800">{p.name}</strong>
                          {p.title && <span> — {p.title}</span>}
                          {p.from && <span className="text-gray-400"> · from {p.from}</span>}
                          {p.date && <span className="text-gray-400"> ({new Date(p.date).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })})</span>}
                        </div>
                      ))}
                      {g.people.length > 6 && <div className="text-xs text-gray-400 italic pl-3">…and {g.people.length - 6} more</div>}
                    </div>
                  ))}
                </div>
              )}

              {/* Backfill Detections — companies losing talent (BD opportunities) */}
              {data.departures && data.departures.length > 0 && (
                <div className="mb-6">
                  <div className="flex items-center justify-between rounded-md px-3 py-1.5 mb-2" style={{ backgroundColor: cssRgb([234, 88, 12]) }}>
                    <span className="text-xs font-bold uppercase tracking-wide text-white">Backfill Detections · companies losing talent</span>
                    <span className="text-xs font-bold text-white">{data.summary.companiesLosing} companies</span>
                  </div>
                  {data.departures.slice(0, 12).map(g => (
                    <div key={g.company} className="mb-2.5 pl-1">
                      <div className="text-sm font-semibold text-gray-900">
                        {g.company} <span className="text-orange-600 font-bold">-{g.count}</span>
                      </div>
                      {g.people.slice(0, 6).map((p, i) => (
                        <div key={i} className="text-xs text-gray-600 pl-3">
                          <strong className="text-gray-800">{p.name}</strong>
                          {p.title && <span> — {p.title}</span>}
                          {p.to && <span className="text-gray-400"> · joined {p.to}</span>}
                          {p.date && <span className="text-gray-400"> ({new Date(p.date).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })})</span>}
                        </div>
                      ))}
                      {g.people.length > 6 && <div className="text-xs text-gray-400 italic pl-3">…and {g.people.length - 6} more</div>}
                    </div>
                  ))}
                </div>
              )}

              {Object.keys(grouped)
                .filter(type => MOVEMENT_TYPES.indexOf(type) < 0)
                .sort((a, b) => grouped[b].length - grouped[a].length)
                .map(type => {
                  const list = grouped[type];
                  const m = metaFor(type);
                  return (
                    <div key={type} className="mb-5">
                      <div
                        className="flex items-center justify-between rounded-md px-3 py-1.5 mb-2"
                        style={{ backgroundColor: cssRgb(m.color) }}
                      >
                        <span className="text-xs font-bold uppercase tracking-wide text-white">{m.label}</span>
                        <span className="text-xs font-bold text-white">{list.length}</span>
                      </div>
                      <div className="space-y-1 pl-1">
                        {list.slice(0, 50).map(s => (
                          <div key={s.id} className="text-sm text-gray-700 flex justify-between gap-3">
                            <span>
                              <span className="text-gray-400 mr-2">{new Date(s.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>
                              <strong className="text-gray-900">{s.candidate_name || s.company || '—'}</strong>
                              {signalDetail(s) && <span className="text-gray-500"> — {signalDetail(s)}</span>}
                            </span>
                            {s.status === 'dismissed' && <span className="text-[10px] text-gray-400 whitespace-nowrap">cleared</span>}
                          </div>
                        ))}
                        {list.length > 50 && <div className="text-xs text-gray-400 italic">…and {list.length - 50} more (in the download)</div>}
                      </div>
                    </div>
                  );
                })}
            </>
          )}
        </div>

        {/* Footer / downloads */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
          <button onClick={downloadCSV} disabled={!data || loading || downloading !== ''}
            className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50">
            {downloading === 'csv' ? 'Downloading…' : 'Download CSV'}
          </button>
          <button onClick={downloadPDF} disabled={!data || loading || downloading !== '' || (data && data.summary.total === 0)}
            className="px-4 py-2 text-sm bg-gray-900 text-white rounded-md hover:bg-black disabled:opacity-50">
            {downloading === 'pdf' ? 'Generating…' : 'Download PDF'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MonitoringReport;
